<template>
    <tr v-on:click="$emit('click')" class="tw:align-middle">
        <td>
            <div class="tw:flex tw:gap-3">
              <div v-if="productTest.product" class="tw:hidden tw:md:block tw:flex-shrink-0">
                <img :src="productTest.product.image" :alt="productTest.product.name" width="70" class="img-fluid">
              </div>

              <div>
                <p v-if="productTest.product" class="mb-0">
                  <strong>{{ productTest.product.name }}</strong>
                  <span class="text-muted" v-if="productTest.product.year">
                      ({{ productTest.product.year }})
                </span>
                  <br>
                  <span class="text-muted">
                  {{ productTest.product.category }}
                </span>
                  <span v-if="productTest.product.brand" class="text-muted">
                  - {{ productTest.product.brand.name }}
                </span>
                </p>

                <div v-if="productTest.equipment && productTest.equipment.barcode_number" class="tw:flex tw:items-center tw:gap-1 text-muted">
                  <QrCodeIcon class="tw:size-4" />
                  <code>{{ productTest.equipment.barcode_number }}</code>
                </div>
              </div>
            </div>
        </td>
        <td v-if="showRegistration" class="tw:hidden tw:md:table-cell tw:print:table-cell">
            <div v-if="productTest.registration" class="tw:items-center">
                <strong>
                    {{ productTest.registration.firstname }} {{ productTest.registration.lastname }}
                </strong>
                <small class="badge badge-dark tw:ml-2" v-if="productTest.registration.company">Pro</small>
            </div>
            <div class="text-muted">
                {{ productTest.tour?.name }}
            </div>
            <div v-if="productTest.registration && productTest.registration.barcode" class="tw:flex tw:items-center tw:gap-1 text-muted">
              <QrCodeIcon class="tw:size-4" />
              <code>{{ productTest.registration.barcode }}</code>
            </div>
        </td>
        <td>
            <div>
              <template v-if="productTest.start_at">
                <div class="tw:flex tw:items-center tw:gap-1">
                  <span class="flex-shrink-0">
                    <CirclePlayIcon class="tw:w-4 tw:h-4" />
                  </span>
                  <span class="tw:font-bold">{{ dayjs(productTest.local_start_at).format("lll") }}</span>
                </div>
                <template v-if="productTest.duration">
                  <div class="tw:flex tw:items-center tw:gap-1">
                    <span class="flex-shrink-0">
                      <TimerIcon class="tw:w-4 tw:h-4" />
                    </span>
                    <Duration :seconds="productTest.duration"/>
                  </div>
                </template>
                <template v-if="!productTest.end_at">
                  <div :class="[
                    'tw:flex tw:items-center tw:gap-1',
                    isLate ? 'tw:text-red-600' : 'tw:text-green-600'
                  ]">
                    <span class="flex-shrink-0">
                      <template v-if="isLate">
                        <ClockAlertIcon class="tw:w-4 tw:h-4" />
                      </template>
                      <template v-else>
                        <TimerIcon class="tw:w-4 tw:h-4" />
                      </template>
                    </span>
                    <span>{{ dayjs(productTest.local_start_at).fromNow(true) }}</span>
                    <small v-if="lateBy">(+ {{ lateBy }})</small>
                  </div>
                </template>
              </template>
              <template v-if="productTest.expected_at && !productTest.end_at">
                <div
                  :class="[
                    'tw:flex tw:items-center tw:gap-1',
                    isLate ? 'tw:text-red-600' : 'tw:text-green-600',
                ]">
                  <span class="flex-shrink-0">
                    <CalendarClockIcon class="tw:w-4 tw:h-4" />
                  </span>
                  <span>{{ dayjs(productTest.local_expected_at).format(dayjs(productTest.local_expected_at).isSame(dayjs(productTest.local_start_at), 'day')  ? 'HH:mm:ss' :"lll") }}</span>
                </div>
              </template>
              <div v-else-if="productTest.end_at"  class="tw:inline-flex tw:items-center tw:gap-1">
                <span class="flex-shrink-0">
                  <CircleStopIcon class="tw:w-4 tw:h-4" />
                </span>
                <span>{{ dayjs(productTest.local_end_at).format(dayjs(productTest.local_end_at).isSame(dayjs(productTest.local_start_at), 'day')  ? 'HH:mm:ss' :"lll") }}</span>
              </div>
            </div>
        </td>
      <td class="tw:hidden tw:lg:table-cell tw:align-middle tw:print:table-cell">
        <div v-if="productTest.productReview">
          <Rating :rating-value="productTest.productReview.rating" :readonly="true" />
        </div>
      </td>

      <td class="tw:hidden tw:sm:table-cell tw:align-middle tw:print:table-cell">
          <template v-if="productTest.status === ProductTestStatusEnum.LATE">
            <Badge variant="red">{{ $t('Late') }}</Badge>
          </template>
          <template v-else-if="productTest.status === ProductTestStatusEnum.IN_PROGRESS">
            <Badge variant="green" class="tw:text-nowrap">{{ $t('In progress') }}</Badge>
          </template>
          <template v-else>
            <Badge variant="zinc">{{ $t('Finished') }}</Badge>
          </template>
        </td>


    </tr>
</template>

<script setup lang="ts">
import dayjs from "dayjs";
import { productTestLateBy } from "@/utils/product-test";
import { ProductTestStatusEnum } from "@/types/enums";
import type { ProductTest } from "@/types/product-test";
import Duration from "../Duration.vue";
import {
  CalendarClockIcon,
  ClockAlertIcon,
  CirclePlayIcon,
  CircleStopIcon,
  QrCodeIcon,
  TimerIcon,
} from "@lucide/vue";
import { Badge } from "@/components/ui/badge/index.js";
import { computed } from "vue";
import Rating from "@/components/Rating.vue";

interface Props {
  productTest: ProductTest;
  showProduct?: boolean;
  showEquipment?: boolean;
  showTour?: boolean;
  showRegistration?: boolean;
  showComment?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  showProduct: false,
  showEquipment: false,
  showTour: false,
  showRegistration: false,
  showComment: false,
})

defineEmits<{ click: [] }>()

const isLate = computed(() => props.productTest.status === ProductTestStatusEnum.LATE)
const lateBy = computed(() => productTestLateBy(props.productTest))
</script>

